import { useSnackbar } from 'notistack'
import { BaseSyntheticEvent, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from 'components/Form'
import { AbTestsAddEditContext } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { useUpdateAbTest } from 'features/ab-tests/hooks/useUpdateAbTest'
import { AbTestsCommonForm } from '../AbTestsCommonForm'
import { abTestsEditSchema, abTestsEditSchemaDefaults } from './abTestsEditSchema'

import type { AbTestsEditSchema } from './abTestsEditSchema'
import type { AbTestsAddEditContextType } from 'features/ab-tests/contexts/AbTestsAddEditContext'

export const AbTestsEditForm = (): React.JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('features/abTests')

  const { abTest, formId, loading, setTouched, setValid } =
    useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)

  if (!abTest) {
    throw Error('AB Test should be accessible')
  }

  const { errors, update } = useUpdateAbTest()

  const handleSubmit = (data: AbTestsEditSchema, event?: BaseSyntheticEvent): void => {
    const submitterId: string | undefined = (event?.nativeEvent as SubmitEvent).submitter?.id

    if (submitterId === undefined) throw new Error('submitterId is missing')
    const { cookieLifetime, name, trafficSplitType, variants } = data

    update({
      cookieLifetime,
      id: abTest.id,
      name,
      status: submitterId.endsWith('save-and-start-button') ? 'active' : 'draft',
      trafficSplitType,
      variants,
    })
  }

  useEffect(() => {
    if (loading) {
      enqueueSnackbar(t('form.saving'), { variant: 'info' })
    }
  }, [loading])

  return (
    <Form<AbTestsEditSchema>
      externalErrors={errors}
      id={formId}
      onSubmit={handleSubmit}
      onTouched={setTouched}
      onValid={setValid}
      schema={abTestsEditSchema}
      schemaDefaults={abTestsEditSchemaDefaults(abTest)}
    >
      {() => <AbTestsCommonForm variant='edit' />}
    </Form>
  )
}
